import { groq } from "next-sanity";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";
import { type Customer } from "./customers";

export type ResourceCategory = {
  _updatedAt: string;
  name: string;
  slug: string;
};

export const getResourceCategories = async (): Promise<ResourceCategory[]> => {
  const query = groq`
    *[_type == "resourceCategory"] {
      _updatedAt,
      name,
      slug
    } | order(_updatedAt desc)
  `;

  try {
    const resourceCategories = await getClient().fetch(query);
    return resourceCategories;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch resource categories: ${errorMessage}`);
    return [];
  }
};

export type ResourceType = {
  _updatedAt: string;
  name: string;
  slug: string;
};

export const getResourceTypes = async (): Promise<ResourceType[]> => {
  const query = groq`
    *[_type == "resourceType"] {
      _updatedAt,
      name,
      slug
    } | order(_updatedAt desc)
  `;

  try {
    const resourceTypes = await getClient().fetch(query);
    return resourceTypes;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch resource types: ${errorMessage}`);
    return [];
  }
};

export type ResourceSimple = {
  title: string;
  subtitle: string;
  generateLandingPage: boolean;
  resourceFileUrl: string;
  resourceFileName: string;
  slug: string;
  publishedAt: string;
  imageUrl?: string;
  category: ResourceCategory;
  type: ResourceType;
};

const resourceSimpleFragment =
  "title, subtitle, generateLandingPage, 'resourceFileUrl': resourceFile.asset->url, resourceFileName, slug, publishedAt, 'imageUrl': cardImage.asset->url, 'category': category->{ name, slug }, 'type': type->{ name, slug }, _updatedAt";

export const getResources = async (): Promise<ResourceSimple[]> => {
  const query = groq`
    *[_type == "resource" && showInDirectory != false ] {
      ${resourceSimpleFragment}
    } | order(publishedAt desc)
  `;

  try {
    const resources = await getClient().fetch(query);
    return resources;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch resources: ${errorMessage}`);
    return [];
  }
};

export type Partner = {
  name: string;
  website: string;
  logoUrl: string;
};

export type ResourcePage = {
  content: string;
  partners?: Partner[];
  posterImageUrl: string;
  hubSpotFormId: string;
  hubSpotCompanyField?: boolean;
  downloadName?: string;
  customers?: Customer[];
  metaTitle?: string;
  metaDescription?: string;
} & ResourceSimple;

export const getResourcePage = async (slug: string): Promise<ResourcePage | undefined> => {
  const query = groq`
    *[_type == "resource" && slug == $slug] {
      ${resourceSimpleFragment},
      content,
      'partners': partners[]->{ name, website, 'logoUrl': logo.asset->url },
      'posterImageUrl': posterImage.asset->url,
      hubSpotFormId,
      hubSpotCompanyField,
      downloadName,
      customers[]->,
      metaTitle,
      metaDescription,
    }
  `;

  try {
    const parameters = { slug };
    const resource = await getClient().fetch(query, parameters);
    return resource[0] ?? undefined;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch resource page with slug "${slug}": ${errorMessage}`);
    return undefined;
  }
};

export type EmbeddedWhitepaper = {
  title: string;
  slug: string;
  fileName: string;
  fileUrl: string;
  posterImageUrl: string;
  hubSpotFormId: string;
  longVariant?: string;
  shortVariant?: string;
  hubSpotCompanyField?: boolean;
};

export const getAllEmbeddedWhitepapers = async (): Promise<EmbeddedWhitepaper[]> => {
  // Only resources that generate landing pages include slug and hubSpotFormId fields.
  const query = groq`
    *[_type == "resource" && generateLandingPage == true] {
      title,
      slug,
      'fileName': resourceFileName,
      'fileUrl': resourceFile.asset->url,
      hubSpotFormId,
      'longVariant': longEmbeddedContent,
      'shortVariant': shortEmbeddedContent,
      'posterImageUrl': posterImage.asset->url,
      hubSpotCompanyField,
    }
  `;

  try {
    const resources = await getClient().fetch(query);
    return resources;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch embedded whitepapers: ${errorMessage}`);
    return [];
  }
};

export const getEmbeddedWhitepaper = async (slug: string): Promise<EmbeddedWhitepaper | undefined> => {
  // Only resources that generate landing pages include slug and hubSpotFormId fields.
  const query = groq`
    *[_type == "resource" && generateLandingPage == true && slug == $slug] {
      title,
      slug,
      'fileName': resourceFileName,
      'fileUrl': resourceFile.asset->url,
      hubSpotFormId,
      hubSpotCompanyField,
      'longVariant': longEmbeddedContent,
      'shortVariant': shortEmbeddedContent,
      'posterImageUrl': posterImage.asset->url,
    }
  `;

  try {
    const parameters = { slug };
    const resource = await getClient().fetch(query, parameters);
    return resource[0] ?? undefined;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch embedded whitepaper with slug "${slug}": ${errorMessage}`);
    return undefined;
  }
};
